/**
 * Effects component that fetches its own data.
 * Uses per-section API endpoints for better performance.
 */
import { IconButton } from '@components/core/icon-button.component';
import { SankeyModal } from '@components/shared/sankey-modal.component';
import { TechnologyEffectsSankeyComponent } from '@components/shared/technology-effects-sankey-chart.component';
import {
  useMissionAssessmentCoreQueryOptions,
  useMissionAssessmentEffectsQueryOptions,
} from '@queries/mission-assessments.query';
import { useGetTechnology } from '@queries/technology.query';
import { useQuery } from '@tanstack/react-query';
import { provenanceItemKeys } from '@utils/mission-assessment-provenance.util';
import { ZoomInIcon } from 'lucide-react';
import { type FunctionComponent, useMemo, useState } from 'react';
import { MissionAssessmentEffectsSkeleton } from './mission-assessment-skeletons.component';
import { TechnologyMissionAssessmentEffectCardComponent } from './technology-mission-assessment-effect-card.component';

type TechnologyMissionAssessmentEffectsProps = {
  missionAssessmentId: string;
  technologyId: string;
};

export const TechnologyMissionAssessmentEffectsComponent: FunctionComponent<TechnologyMissionAssessmentEffectsProps> = (
  props,
) => {
  const { missionAssessmentId, technologyId } = props;

  const { data: effectsData, isLoading } = useQuery(useMissionAssessmentEffectsQueryOptions(missionAssessmentId));
  const { data: coreData } = useQuery(useMissionAssessmentCoreQueryOptions(missionAssessmentId));
  const { data: technology } = useGetTechnology(technologyId);

  const [isHovering, setIsHovering] = useState(false);
  const [isOpenFullScreenModal, setIsOpenFullScreenModal] = useState(false);

  const effects = effectsData?.effects ?? [];
  const hasEffects = effects.length > 0;

  // Prepare data for the Sankey component
  const sankeyData = useMemo(() => {
    if (!hasEffects || !technology?.category || !technology) return [];
    return [
      {
        category: technology.category,
        effects,
        technology,
      },
    ];
  }, [effects, hasEffects, technology]);

  if (isLoading) {
    return <MissionAssessmentEffectsSkeleton />;
  }

  return (
    <>
      <div
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {hasEffects ? (
          effects.map((effect, index) => {
            const isPrimary = index === 0;
            const useTooltip = effect.use.description;

            return (
              <TechnologyMissionAssessmentEffectCardComponent
                effect={effect}
                isPrimary={isPrimary}
                key={`${effect.use.id}-${effect.domain.id}-${effect.effect.id}-${index}`}
                provenance={
                  coreData?.provenance?.[provenanceItemKeys.effect(effect.effect.id, effect.use.id, effect.domain.id)]
                }
                useTooltip={useTooltip}
              />
            );
          })
        ) : (
          <div className="text-xs text-gray-500">No data available</div>
        )}

        {hasEffects && technology?.category && sankeyData.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4 group">
              <div className="text-sm font-medium">Technology Effects</div>
              <IconButton
                icon={<ZoomInIcon />}
                isVisible={isHovering}
                onClick={() => setIsOpenFullScreenModal(true)}
                variant="secondary"
              />
            </div>
            <div>
              <TechnologyEffectsSankeyComponent
                data={sankeyData}
                sankeyProps={{
                  maxHeight: 600,
                  onOpenFullScreen: () => setIsOpenFullScreenModal(true),
                }}
              />
            </div>
          </div>
        )}
      </div>

      {isOpenFullScreenModal && (
        <SankeyModal
          isOpen={isOpenFullScreenModal}
          onClose={() => setIsOpenFullScreenModal(false)}
          title={`Technology Effects - ${technology?.name || 'Unknown Technology'}`}
        >
          {({ selectedTechnologyId, settings }) => (
            <TechnologyEffectsSankeyComponent
              data={sankeyData}
              settings={settings}
              technologyId={selectedTechnologyId}
            />
          )}
        </SankeyModal>
      )}
    </>
  );
};
